import { 
	_capitalizeText,
	_formatNumber,
	_formatPrice,
	_nameToUrl,
	_shouldShow,
	_getBuildingURL,
	buildingFL,
	_replaceAbbreviations,
	_objectEqual,
	StatusBadge,
	_getUnitFloorPlan,
	UnitCurrentStatus,
	_formatUnitNumber,
	HouseCurrentStatus,
	_getFirstAndLastFromFullName,
	_extractIndividualNames,
	_isHouse,
	OwnershipTypeBadge
} from "../../../shared/helpers/utils";

function _safeStr(v) {
  if (!v && v !== 0) return '';
  return v.toString();
}

function _pickAge(person) {
  if (person && typeof person.age !== 'undefined' && person.age !== null) {
	return _safeStr(person.age);
  }
  if (person && person.dob && typeof person.dob.age !== 'undefined') {
	return _safeStr(person.dob.age);
  }
  return '';
}

function _mapAddresses(person) {
  var out = [];
  var addrs = person && person.addresses ? person.addresses : [];

  for (var i = 0; i < addrs.length; i++) {
	var a = addrs[i] || {};

	// Prefer structured fields from your new provider
	var houseNumber = a.houseNumber || a.HouseNumber || '';
	var streetPreDirection = a.streetPreDirection || a.StreetPreDirection || '';
	var streetName = a.streetName || a.StreetName || '';
	var streetType = a.streetType || a.StreetType || '';
	var streetPostDirection = a.streetPostDirection || a.StreetPostDirection || '';

	// Unit
	var unit =
	  a.unit ||
	  a.Unit ||
	  a.addressLine2 ||
	  a.AddressLine2 ||
	  a.streetLine2 ||
	  a.StreetLine2 ||
	  '';

	var city = a.city || a.City || '';
	var state = a.state || a.State || '';
	var zip =
	  a.zip ||
	  a.Zip ||
	  a.postalCode ||
	  a.PostalCode ||
	  '';

	// Dates
	var firstReportedDate =
	  a.firstReportedDate ||
	  a.publicFirstSeenDate ||
	  a.totalFirstSeenDate ||
	  a.firstSeenDate ||
	  a.startDate ||
	  '';
	var lastReportedDate =
	  a.lastReportedDate ||
	  a.lastSeenDate ||
	  a.endDate ||
	  '';

	// Build street from components if possible
	var streetParts = [];
	if (houseNumber) streetParts.push(houseNumber);
	if (streetPreDirection) streetParts.push(streetPreDirection);
	if (streetName) streetParts.push(streetName);
	if (streetType) streetParts.push(streetType);
	if (streetPostDirection) streetParts.push(streetPostDirection);

	var street = streetParts.join(' ').replace(/\s+/g, ' ').trim();

	// Fallback: try fullAddress if street is still empty
	if (!street && a.fullAddress) {
	  // e.g. "3204 3a St NE; Great Falls, MT 59404-1070"
	  var full = a.fullAddress.toString();
	  var semiIdx = full.indexOf(';');
	  if (semiIdx !== -1) {
		street = full.substring(0, semiIdx).trim();
	  } else {
		street = full.trim();
	  }
	}

	// Skip clearly bad / empty addresses
	var hasBasic =
	  (street && street.length > 0) ||
	  (city && city.length > 0) ||
	  (zip && zip.toString().trim().length > 0);

	if (!hasBasic) {
	  continue;
	}

	// Optionally prefer deliverable addresses, but still allow others if needed
	// If you want to only include deliverable ones, uncomment this:
	// if (a.isDeliverable === false) { continue; }

	out.push({
	  firstReportedDate: _safeStr(firstReportedDate),
	  lastReportedDate: _safeStr(lastReportedDate),
	  street: _safeStr(street),
	  unit: _safeStr(unit),
	  city: _safeStr(city),
	  state: _safeStr(state),
	  zip: _safeStr(zip)
	});
  }

  return out;
}


function _mapEmails(person) {
  var out = [];
  var seen = {};
  var arr = person && person.emailAddresses ? person.emailAddresses : [];

  for (var i = 0; i < arr.length; i++) {
	var em = arr[i] || {};

	// source fields
	var rawEmail = em.emailAddress || em.email || '';
	if (!rawEmail || !rawEmail.toString().trim()) {
	  continue;
	}

	var email = rawEmail.toString().trim().toLowerCase();

	// dedupe on lowercase email
	if (seen[email]) {
	  continue;
	}
	seen[email] = true;

	// nonBusiness: 1 = personal, 0 = business (per your provider)
	var isBusiness = false;
	if (typeof em.nonBusiness !== 'undefined' && em.nonBusiness !== null) {
	  isBusiness = em.nonBusiness === 0;
	} else if (typeof em.isBusiness !== 'undefined') {
	  isBusiness = !!em.isBusiness;
	}

	// validation: fall back to deliverability if present, else false
	var isValidated = false;
	if (typeof em.isValidated !== 'undefined') {
	  isValidated = !!em.isValidated;
	} else if (typeof em.isDeliverable !== 'undefined') {
	  isValidated = !!em.isDeliverable;
	}

	out.push({
	  email: _safeStr(rawEmail),
	  isValidated: isValidated,
	  isBusiness: isBusiness
	});
  }

  return out;
}

function _mapName(person) {
  var n = person && person.name ? person.name : {};
  return {
	firstName: _safeStr(n.firstName || ''),
	middleName: _safeStr(n.middleName || ''),
	lastName: _safeStr(n.lastName || '')
  };
}

function _normalizePhoneType(raw) {
  if (!raw) return '';
  var v = raw.toString().toLowerCase();

  // common mappings from provider values to your internal labels
  if (v.indexOf('wireless') !== -1 || v.indexOf('mobile') !== -1 || v.indexOf('cell') !== -1) {
	return 'mobile';
  }
  if (v.indexOf('landline') !== -1 || v.indexOf('residential') !== -1 || v.indexOf('business') !== -1) {
	return 'landline';
  }
  if (v.indexOf('voip') !== -1 || v.indexOf('internet') !== -1) {
	return 'voip';
  }

  // fallback to cleaned provider string if nothing matches
  return v;
}

function _mapPhones(person) {
  var out = [];
  var arr = person && person.phoneNumbers ? person.phoneNumbers : [];

  for (var i = 0; i < arr.length; i++) {
	var p = arr[i] || {};
	var number = p.phoneNumber || p.number || '';

	if (!number || !number.toString().trim()) {
	  continue;
	}

	var type = p.phoneType || p.type || p.lineType || '';
	var normalizedType = _normalizePhoneType(type);

	// isConnected: prefer explicit flag, then fall back on connectionStatus if present
	var isConnected = false;
	if (typeof p.isConnected !== 'undefined') {
	  isConnected = !!p.isConnected;
	} else if (typeof p.connectionStatus !== 'undefined' && p.connectionStatus) {
	  var cs = p.connectionStatus.toString().toLowerCase();
	  if (cs.indexOf('disc') === -1 && cs.indexOf('disconnected') === -1 && cs.indexOf('cancel') === -1) {
		isConnected = true;
	  }
	}

	// dates: prefer explicit first/lastReportedDate, then seen/start/end fields
	var firstReportedDate =
	  p.firstReportedDate ||
	  p.publicFirstSeenDate ||
	  p.totalFirstSeenDate ||
	  p.firstSeenDate ||
	  p.startDate ||
	  '';
	var lastReportedDate =
	  p.lastReportedDate ||
	  p.lastSeenDate ||
	  p.endDate ||
	  '';

	// if you want to skip non-connected numbers entirely, uncomment:
	// if (!isConnected) { continue; }

	out.push({
	  number: _safeStr(number),
	  type: _safeStr(normalizedType),
	  isConnected: isConnected,
	  firstReportedDate: _safeStr(firstReportedDate),
	  lastReportedDate: _safeStr(lastReportedDate)
	});
  }

  return out;
}


/**
 * Convert a raw person record from the provider format
 * into your internal owner format:
 *
 * {
 *   addresses: [...],
 *   age: "47",
 *   emails: [...],
 *   name: { firstName, middleName, lastName },
 *   phones: [...]
 * }
 */
export function mapPersonToOwnerFormat(person) {
  if (!person) {
	return {
	  addresses: [],
	  age: '',
	  emails: [],
	  name: { firstName: '', middleName: '', lastName: '' },
	  phones: []
	};
  }
console.log(person)
  return {
	addresses: _mapAddresses(person),
	age: _pickAge(person),
	emails: _mapEmails(person),
	name: _mapName(person),
	phones: _mapPhones(person)
  };
}

function _clean(s) {
  if (!s) return '';
  return s.toString().toLowerCase().replace(/\s+/g, ' ').trim();
}

function _lev(a, b) {
  a = _clean(a);
  b = _clean(b);
  var al = a.length;
  var bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;

  var dp = [];
  var i;
  for (i = 0; i <= al; i++) {
	dp[i] = [];
	dp[i][0] = i;
  }
  var j;
  for (j = 0; j <= bl; j++) {
	dp[0][j] = j;
  }
  for (i = 1; i <= al; i++) {
	var ca = a.charAt(i - 1);
	for (j = 1; j <= bl; j++) {
	  var cb = b.charAt(j - 1);
	  var cost = ca === cb ? 0 : 1;
	  var del = dp[i - 1][j] + 1;
	  var ins = dp[i][j - 1] + 1;
	  var sub = dp[i - 1][j - 1] + cost;
	  var m = del < ins ? del : ins;
	  dp[i][j] = m < sub ? m : sub;
	}
  }
  return dp[al][bl];
}

function _sim(a, b) {
  a = _clean(a);
  b = _clean(b);
  var maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  var d = _lev(a, b);
  return 1 - d / maxLen;
}

function _stripPunct(s) {
  if (!s) return '';
  return s.replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

function _normState(s) {
  if (!s) return '';
  var v = s.toString().toLowerCase().trim();
  var map = {
	'new york': 'ny', ny: 'ny',
	'new jersey': 'nj', nj: 'nj',
	california: 'ca', ca: 'ca',
	florida: 'fl', fl: 'fl',
	connecticut: 'ct', ct: 'ct',
	massachusetts: 'ma', ma: 'ma'
  };
  return map[v] || v;
}

function _normZip5(z) {
  if (!z) return '';
  var m = z.toString().match(/\d{5}/);
  return m ? m[0] : '';
}

function _normStreetText(s) {
  if (!s) return '';
  var v = _stripPunct(s.toLowerCase());
  v = v.replace(/\b(apartment|apt\.?)\b/g, 'apt');
  v = v.replace(/\b(unit|ste\.?|suite)\b/g, 'apt');

  v = v
	.replace(/\bstreet\b/g, 'st')
	.replace(/\bavenue\b/g, 'ave')
	.replace(/\broad\b/g, 'rd')
	.replace(/\bboulevard\b/g, 'blvd')
	.replace(/\bplace\b/g, 'pl')
	.replace(/\bdrive\b/g, 'dr')
	.replace(/\blane\b/g, 'ln')
	.replace(/\bcourt\b/g, 'ct')
	.replace(/\bterrace\b/g, 'ter')
	.replace(/\bparkway\b/g, 'pkwy');

  v = v.replace(/\s+/g, ' ').trim();
  return v;
}

function _normAddrString(addr1, addr2, city, state, zip) {
  var a1 = _normStreetText(addr1 || '');
  var a2 = _normStreetText(addr2 || '');
  var c = _stripPunct((city || '').toLowerCase());
  var st = _normState(state || '');
  var z5 = _normZip5(zip || '');
  var parts = [];
  if (a1) parts.push(a1);
  if (a2) parts.push(a2);
  if (c) parts.push(c);
  if (st) parts.push(st);
  if (z5) parts.push(z5);
  return parts.join(' ');
}

function _tokenSet(s) {
  var out = {};
  var arr = (s || '').split(/\s+/);
  for (var i = 0; i < arr.length; i++) {
	var t = arr[i].trim();
	if (t) out[t] = true;
  }
  return out;
}

function _jaccard(a, b) {
  var A = _tokenSet(a);
  var B = _tokenSet(b);
  var inter = 0;
  var uni = 0;
  var k;
  for (k in A) {
	if (A.hasOwnProperty(k)) {
	  if (B[k]) inter++;
	  uni++;
	}
  }
  for (k in B) {
	if (B.hasOwnProperty(k) && !A[k]) uni++;
  }
  if (uni === 0) return 1;
  return inter / uni;
}

function _houseAndUnitMatch(normA, normB) {
  var numA = (normA.match(/\b\d+\b/) || [''])[0];
  var numB = (normB.match(/\b\d+\b/) || [''])[0];
  if (!numA || !numB || numA !== numB) return false;

  var ta = normA.split(/\s+/);
  var tb = normB.split(/\s+/);
  var stemA = '';
  var stemB = '';
  var i;

  for (i = 0; i < ta.length; i++) {
	if (ta[i] === numA) {
	  stemA = (ta[i + 1] || '') + ' ' + (ta[i + 2] || '');
	  break;
	}
  }
  for (i = 0; i < tb.length; i++) {
	if (tb[i] === numB) {
	  stemB = (tb[i + 1] || '') + ' ' + (tb[i + 2] || '');
	  break;
	}
  }
  stemA = stemA.trim();
  stemB = stemB.trim();
  if (!stemA || !stemB) return false;

  if (_sim(stemA, stemB) < 0.98) return false;

  var ua = (normA.match(/\bapt\s*\w+\b/) || [''])[0];
  var ub = (normB.match(/\bapt\s*\w+\b/) || [''])[0];
  if (ua && ub) {
	return _sim(ua, ub) >= 0.98;
  }
  return true;
}

function _addressScore(person, addr1, addr2, city, state, zip) {
  var tFull = _normAddrString(addr1, addr2, city, state, zip);
  var tLine = _normAddrString(addr1, '', '', '', '');
  var tLineUnit = _normAddrString(addr1, addr2, '', '', '');
  var targets = [];
  if (tLine) targets.push(tLine);
  if (tLineUnit) targets.push(tLineUnit);
  if (tFull) targets.push(tFull);

  var best = 0;
  var addrs = person && person.addresses ? person.addresses : [];
  var i;

  for (i = 0; i < addrs.length; i++) {
	var a = addrs[i] || {};
	var c1 = _normAddrString(
	  a.addressLine1 || a.AddressLine1 || a.streetLine1 || a.StreetLine1 || '',
	  a.addressLine2 || a.AddressLine2 || a.streetLine2 || a.StreetLine2 || '',
	  a.city || a.City || '',
	  a.state || a.State || '',
	  a.zip || a.Zip || a.postalCode || a.PostalCode || ''
	);
	var c2 = _normAddrString(
	  a.addressLine1 || a.AddressLine1 || a.streetLine1 || a.StreetLine1 || '',
	  a.addressLine2 || a.AddressLine2 || a.streetLine2 || a.StreetLine2 || '',
	  '',
	  '',
	  ''
	);
	var c3 = _normStreetText(a.fullAddress || a.FullAddress || '');

	var cands = [];
	if (c1) cands.push(c1);
	if (c2) cands.push(c2);
	if (c3) cands.push(c3);

	for (var ti = 0; ti < targets.length; ti++) {
	  var T = targets[ti];
	  for (var cj = 0; cj < cands.length; cj++) {
		var C = cands[cj];

		if (_houseAndUnitMatch(T, C)) return 1.0;

		var s1 = _sim(C, T);
		var s2 = _jaccard(C, T);
		var s = s1 > s2 ? s1 : s2;
		if (s > best) best = s;
	  }
	}
  }
  return best;
}

function _stateMatchBool(person, state) {
  var target = _clean(state);
  var addrs = person && person.addresses ? person.addresses : [];
  if (addrs && addrs.length > 0) {
	for (var i = 0; i < addrs.length; i++) {
	  var a = addrs[i];
	  var st = a && (a.state || a.State) ? _clean(a.state || a.State) : '';
	  if (st && st === target) return true;
	}
  }
  return false;
}

function _primaryFirstLastScores(person, first, last) {
  var F = first || '';
  var L = last || '';
  var pf =
	person && person.name && person.name.firstName
	  ? person.name.firstName
	  : '';
  var pl =
	person && person.name && person.name.lastName
	  ? person.name.lastName
	  : '';
  var firstPrimary = _sim(pf, F);
  var lastPrimary = _sim(pl, L);
  var nsBoth = firstPrimary < lastPrimary ? firstPrimary : lastPrimary;
  var fullPrimaryExact = firstPrimary >= 0.999 && lastPrimary >= 0.999 ? 1 : 0;
  return {
	firstPrimary: firstPrimary,
	lastPrimary: lastPrimary,
	nsBoth: nsBoth,
	fullPrimaryExact: fullPrimaryExact
  };
}

/**
 * Filter and sort persons by address + name quality.
 * Returns a new array, sorted best first.
 */
export function filterAndSortPersons(
  persons,
  first,
  last,
  state,
  addr1,
  addr2,
  city,
  zip
) {
  var NAME_OK = 0.9;
  var ADDR_OK = 0.9;
  var ADDR_EXACT = 0.98;
  var out = [];
  var i;

  for (i = 0; i < (persons ? persons.length : 0); i++) {
	var p = persons[i];

	var ns = _primaryFirstLastScores(p, first, last);
	var nsFirst = ns.firstPrimary;
	var nsLast = ns.lastPrimary;
	var nsBoth = ns.nsBoth;

	var as = _addressScore(p, addr1, addr2, city, state, zip);

	var keep = as >= ADDR_OK || (nsFirst >= NAME_OK && nsLast >= NAME_OK);
	if (!keep) continue;

	var addrBucket = as >= ADDR_EXACT ? 2 : as >= ADDR_OK ? 1 : 0;
	var stMatch = _stateMatchBool(p, state) ? 1 : 0;

	p.__nsBoth = nsBoth;
	p.__as = as;
	p.__addrBucket = addrBucket;
	p.__state = stMatch;
	p.__ph = p && p.phoneNumbers ? p.phoneNumbers.length : 0;
	p.__em = p && p.emailAddresses ? p.emailAddresses.length : 0;

	out.push(p);
  }

  out.sort(function (a, b) {
	if (b.__addrBucket !== a.__addrBucket) return b.__addrBucket - a.__addrBucket;
	if (b.__nsBoth !== a.__nsBoth) return b.__nsBoth > a.__nsBoth ? 1 : -1;
	if (b.__as !== a.__as) return b.__as > a.__as ? 1 : -1;
	if (b.__state !== a.__state) return b.__state - a.__state;
	if (b.__ph !== a.__ph) return b.__ph - a.__ph;
	if (b.__em !== a.__em) return b.__em - a.__em;
	return 0;
  });

  for (var k = 0; k < out.length; k++) {
	delete out[k].__nsBoth;
	delete out[k].__as;
	delete out[k].__addrBucket;
	delete out[k].__state;
	delete out[k].__ph;
	delete out[k].__em;
  }
  return out;
}

/**
 * Convenience helper for "best match only"
 */
 function _getMinimumNameMatchTier(p, first, last) {
   function _norm(str) {
	 return String(str || "")
	   .toLowerCase()
	   .replace(/[^a-z]/g, "");
   }
 
   var tf = _norm(first);
   var tl = _norm(last);
 
   // If caller did not supply a name, do not gate
   if (!tf && !tl) return "primary";
 
   function _matchesPair(f, l) {
	 var cf = _norm(f);
	 var cl = _norm(l);
 
	 if (tl) {
	   if (!cl || cl !== tl) return false;
	 }
 
	 if (tf) {
	   if (!cf) return false;
	   if (cf === tf) return true;
	   if (cf.charAt(0) === tf.charAt(0)) return true;
	   return false;
	 }
 
	 return true;
   }
 
   if (!p) return null;
 
   // 1) primary names first
   if (p.name && _matchesPair(p.name.firstName, p.name.lastName)) {
	 return "primary";
   }
 
   if (p.names && p.names.length) {
	 for (var i = 0; i < p.names.length; i++) {
	   var n = p.names[i];
	   if (n && _matchesPair(n.firstName, n.lastName)) {
		 return "primary";
	   }
	 }
   }
 
   // 2) only then consider AKAs
   if (p.akas && p.akas.length) {
	 for (var j = 0; j < p.akas.length; j++) {
	   var a = p.akas[j];
	   if (a && _matchesPair(a.firstName, a.lastName)) {
		 return "aka";
	   }
	 }
   }
 
   return null;
 }


export function pickBestPerson(
   persons,
   first,
   last,
   state,
   addr1,
   addr2,
   city,
   zip
 ) {
   // no persons at all
   if (!persons || persons.length === 0) {
	 return null;
   }
 
   function _getMinimumNameMatchTier(p, firstName, lastName) {
	 function _norm(str) {
	   return String(str || "")
		 .toLowerCase()
		 .replace(/[^a-z]/g, "");
	 }
 
	 var tf = _norm(firstName);
	 var tl = _norm(lastName);
 
	 // If caller did not supply a name, do not gate
	 if (!tf && !tl) return "primary";
 
	 function _matchesPair(f, l) {
	   var cf = _norm(f);
	   var cl = _norm(l);
 
	   // Require last-name match if provided
	   if (tl) {
		 if (!cl || cl !== tl) return false;
	   }
 
	   // If first name provided, require exact or first-initial match
	   if (tf) {
		 if (!cf) return false;
		 if (cf === tf) return true;
		 if (cf.charAt(0) === tf.charAt(0)) return true;
		 return false;
	   }
 
	   return true;
	 }
 
	 if (!p) return null;
 
	 // 1) primary names first
	 if (p.name && _matchesPair(p.name.firstName, p.name.lastName)) {
	   return "primary";
	 }
 
	 if (p.names && p.names.length) {
	   for (var i = 0; i < p.names.length; i++) {
		 var n = p.names[i];
		 if (n && _matchesPair(n.firstName, n.lastName)) {
		   return "primary";
		 }
	   }
	 }
 
	 // 2) only then consider AKAs
	 if (p.akas && p.akas.length) {
	   for (var j = 0; j < p.akas.length; j++) {
		 var a = p.akas[j];
		 if (a && _matchesPair(a.firstName, a.lastName)) {
		   return "aka";
		 }
	   }
	 }
 
	 return null;
   }
 
   // 1) strict filter first (same behavior as before)
   var arr = filterAndSortPersons(
	 persons,
	 first,
	 last,
	 state,
	 addr1,
	 addr2,
	 city,
	 zip
   );
   if (arr && arr.length > 0) {
	 return arr[0];
   }
 
   // 2) fallback: score all persons but prefer:
   //    - has phone or email
   //    - has a not-awful address match
   //    - NAME LOGIC: prefer primary-name matches; only use AKAs if there are no primary matches at all
   var MIN_ADDR_SCORE = 0.3; // tweak if you want stricter or looser
 
   var primaryAll = [];
   var primaryPreferred = [];
   var akaAll = [];
   var akaPreferred = [];
   var touched = [];
 
   for (var i = 0; i < persons.length; i++) {
	 var p = persons[i];
 
	 var tier = _getMinimumNameMatchTier(p, first, last);
	 if (!tier) continue;
 
	 var ns = _primaryFirstLastScores(p, first, last);
	 var as = _addressScore(p, addr1, addr2, city, state, zip);
	 var stMatch = _stateMatchBool(p, state) ? 1 : 0;
	 var ph = p && p.phoneNumbers ? p.phoneNumbers.length : 0;
	 var em = p && p.emailAddresses ? p.emailAddresses.length : 0;
 
	 var hasContact = ph > 0 || em > 0; // require at least phone OR email
	 var addrOk = as >= MIN_ADDR_SCORE;
 
	 // attach scoring fields
	 p.__nsBothLoose = ns.nsBoth;
	 p.__asLoose = as;
	 p.__stateLoose = stMatch;
	 p.__phLoose = ph;
	 p.__emLoose = em;
 
	 touched.push(p);
 
	 if (tier === "primary") {
	   primaryAll.push(p);
	   if (hasContact && addrOk) primaryPreferred.push(p);
	 } else {
	   akaAll.push(p);
	   if (hasContact && addrOk) akaPreferred.push(p);
	 }
   }
 
   // If no primary matches, allow AKA matches; otherwise ignore AKAs completely.
   var scoredAll = primaryAll.length > 0 ? primaryAll : akaAll;
   var scoredPreferred = primaryAll.length > 0 ? primaryPreferred : akaPreferred;
 
   // If name-gate filtered everything out, do not pick a random person
   if (scoredAll.length === 0) {
	 for (i = 0; i < touched.length; i++) {
	   delete touched[i].__nsBothLoose;
	   delete touched[i].__asLoose;
	   delete touched[i].__stateLoose;
	   delete touched[i].__phLoose;
	   delete touched[i].__emLoose;
	 }
	 return null;
   }
 
   // choose preferred group if we have any, else fall back to all
   var arrToUse = scoredPreferred.length > 0 ? scoredPreferred : scoredAll;
 
   arrToUse.sort(function (a, b) {
	 if (b.__asLoose !== a.__asLoose) {
	   return b.__asLoose > a.__asLoose ? 1 : -1;
	 }
	 if (b.__nsBothLoose !== a.__nsBothLoose) {
	   return b.__nsBothLoose > a.__nsBothLoose ? 1 : -1;
	 }
	 if (b.__stateLoose !== a.__stateLoose) {
	   return b.__stateLoose - a.__stateLoose;
	 }
	 if (b.__phLoose !== a.__phLoose) {
	   return b.__phLoose - a.__phLoose;
	 }
	 if (b.__emLoose !== a.__emLoose) {
	   return b.__emLoose - a.__emLoose;
	 }
	 return 0;
   });
 
   var best = arrToUse[0] || null;
 
   // clean temp fields on all persons we touched
   for (i = 0; i < touched.length; i++) {
	 delete touched[i].__nsBothLoose;
	 delete touched[i].__asLoose;
	 delete touched[i].__stateLoose;
	 delete touched[i].__phLoose;
	 delete touched[i].__emLoose;
   }
 
   return best;
 }




// utils/skipTraceUtils.js (or same file)
 
 function _normalizeNamePart(str) {
   return String(str || '')
	 .toLowerCase()
	 .replace(/[^a-z]/g, '');
 }
 
 function _getRecordPrimaryName(rec) {
   // Prefer top-level first/lastName, fall back to names[0]
   var first = '';
   var last  = '';
 
   if (rec) {
	 if (rec.firstName) first = rec.firstName;
	 if (rec.lastName)  last  = rec.lastName;
 
	 if ((!first || !last) && rec.names && rec.names.length > 0) {
	   var n0 = rec.names[0];
	   if (!first && n0 && n0.firstName) first = n0.firstName;
	   if (!last  && n0 && n0.lastName)  last  = n0.lastName;
	 }
   }
 
   return {
	 first: _normalizeNamePart(first),
	 last:  _normalizeNamePart(last)
   };
 }
 
 function _getRecordLocation(rec) {
   // Try addresses[0], then personLocation[0]
   var city = '';
   var state = '';
 
   if (rec && rec.addresses && rec.addresses.length > 0) {
	 var a0 = rec.addresses[0];
	 if (a0 && a0.city)  city  = a0.city;
	 if (a0 && a0.state) state = a0.state;
   } else if (rec && rec.personLocation && rec.personLocation.length > 0) {
	 var p0 = rec.personLocation[0];
	 if (p0 && p0.city)  city  = p0.city;
	 if (p0 && p0.state) state = p0.state;
   }
 
   return {
	 city:  String(city || '').toLowerCase(),
	 state: String(state || '').toLowerCase()
   };
 }
 function _getRecordAddressLocation(rec) {
   var city = '';
   var state = '';
 
   if (rec && rec.addresses && rec.addresses.length > 0) {
	 var a0 = rec.addresses[0];
	 if (a0) {
	   if (a0.city)  city  = a0.city;
	   if (a0.state) state = a0.state;
	 }
   }
 
   return {
	 city:  String(city || '').toLowerCase(),
	 state: String(state || '').toLowerCase()
   };
 }
 
 function _getRecordPersonLocation(rec) {
   var city = '';
   var state = '';
 
   if (rec && rec.personLocation && rec.personLocation.length > 0) {
	 var p0 = rec.personLocation[0];
	 if (p0) {
	   if (p0.city)  city  = p0.city;
	   if (p0.state) state = p0.state;
	 }
   }
 
   return {
	 city:  String(city || '').toLowerCase(),
	 state: String(state || '').toLowerCase()
   };
 }

 function _getRecordPhones(rec) {
   var out = [];
   if (!rec) return out;
 
   function _addPhone(str) {
	 var digits = String(str || '').replace(/\D/g, '');
	 if (digits.length >= 7) out.push(digits);
   }
 
   // phoneNumbers top-level array
   if (rec.phoneNumbers && rec.phoneNumbers.length > 0) {
	 for (var i = 0; i < rec.phoneNumbers.length; i++) {
	   _addPhone(rec.phoneNumbers[i]);
	 }
   }
 
   // tahoeAppend landline/mobile
   if (rec.tahoeAppend) {
	 var ta = rec.tahoeAppend;
	 if (ta.mobilePhones && ta.mobilePhones.length > 0) {
	   for (var j = 0; j < ta.mobilePhones.length; j++) {
		 _addPhone(ta.mobilePhones[j]);
	   }
	 }
	 if (ta.landlinePhones && ta.landlinePhones.length > 0) {
	   for (var k = 0; k < ta.landlinePhones.length; k++) {
		 _addPhone(ta.landlinePhones[k]);
	   }
	 }
   }
 
   return out;
 }
 
 function _scoreRecordRichness(rec) {
   if (!rec) return 0;
 
   var score = 0;
 
   // Emails (direct + tahoeAppend)
   var directEmails = rec.emailAddresses || [];
   var appendEmails = (rec.tahoeAppend && rec.tahoeAppend.emails) ? rec.tahoeAppend.emails : [];
 
   var totalEmails = 0;
   if (directEmails && directEmails.length) totalEmails += directEmails.length;
   if (appendEmails && appendEmails.length) totalEmails += appendEmails.length;
 
   if (totalEmails > 0) {
	 score += 5;           // has at least one email
	 if (totalEmails >= 3) score += 5;  // richer set of emails
	 if (totalEmails >= 8) score += 5;  // very rich set
   }
 
   // Phones (direct + tahoeAppend)
   var phoneCount = 0;
 
   if (rec.phoneNumbers && rec.phoneNumbers.length) {
	 phoneCount += rec.phoneNumbers.length;
   }
   if (rec.tahoeAppend && rec.tahoeAppend.mobilePhones && rec.tahoeAppend.mobilePhones.length) {
	 phoneCount += rec.tahoeAppend.mobilePhones.length;
   }
   if (rec.tahoeAppend && rec.tahoeAppend.landlinePhones && rec.tahoeAppend.landlinePhones.length) {
	 phoneCount += rec.tahoeAppend.landlinePhones.length;
   }
 
   if (phoneCount > 0) {
	 score += 5;            // has at least one phone
	 if (phoneCount >= 3) score += 5;   // multiple numbers
   }
 
   // Current employment and work history
   if (rec.currentEmployment && rec.currentEmployment.length) {
	 score += 10;  // strong signal for a good workplace profile
   }
 
   if (rec.workExperience && rec.workExperience.length) {
	 score += 5;           // some history
	 if (rec.workExperience.length >= 3) {
	   score += 5;         // richer work history
	 }
   }
 
   // Education
   if (rec.education && rec.education.length) {
	 score += 5;
   }
 
   // Person locations: more than one location often means richer profile
   if (rec.personLocation && rec.personLocation.length > 1) {
	 score += 3;
   }
 
   // Social / URLs
   if (rec.personUrls && rec.personUrls.length) {
	 score += 5;
   }
 
   if (rec.socialConnections) {
	 score += 3;
   }
 
   return score;
 }
 
 export function pickBestWorkplaceRecord(personInput, records) {
   if (!records || !records.length) return null;
 
   // ----------------------------
   // Scoped helpers (local only)
   // ----------------------------
   function _normLower(s) {
	 return String(s || '').trim().toLowerCase();
   }
 
   function _digitsOnly(s) {
	 return String(s || '').replace(/\D/g, '');
   }
 
   function _safeArr(a) {
	 return Array.isArray(a) ? a : [];
   }
 
   function _getEmploymentLoc(rec) {
	 var ce = _safeArr(rec && rec.currentEmployment);
	 if (ce.length && ce[0]) {
	   return { city: _normLower(ce[0].city), state: _normLower(ce[0].state) };
	 }
	 return { city: '', state: '' };
   }
 
   function _getPersonLoc(rec) {
	 var pl = _safeArr(rec && rec.personLocation);
	 if (pl.length && pl[0]) {
	   return { city: _normLower(pl[0].city), state: _normLower(pl[0].state) };
	 }
	 return { city: '', state: '' };
   }
 
   function _getAllAddresses(rec) {
	 var out = [];
	 var addrs = _safeArr(rec && rec.addresses);
	 for (var i = 0; i < addrs.length; i++) {
	   var a = addrs[i] || {};
	   out.push({ city: _normLower(a.city), state: _normLower(a.state) });
	 }
	 return out;
   }
 
   function _hasAnyAddressState(rec, inputState) {
	 if (!inputState) return false;
	 var addrs = _getAllAddresses(rec);
	 for (var i = 0; i < addrs.length; i++) {
	   if (addrs[i].state && addrs[i].state === inputState) return true;
	 }
	 return false;
   }
 
   function _hasAnyAddressCityState(rec, inputCity, inputState) {
	 if (!inputCity || !inputState) return false;
	 var addrs = _getAllAddresses(rec);
	 for (var i = 0; i < addrs.length; i++) {
	   if (addrs[i].city === inputCity && addrs[i].state === inputState) return true;
	 }
	 return false;
   }
 
   function _hasAnyAddressDifferentState(rec, inputState) {
	 if (!inputState) return false;
	 var addrs = _getAllAddresses(rec);
	 for (var i = 0; i < addrs.length; i++) {
	   if (addrs[i].state && addrs[i].state !== inputState) return true;
	 }
	 return false;
   }
 
   function _stateCityScore(cityAndStateEq, stateEq, stateDiff) {
	 var s = 0;
	 if (cityAndStateEq) s += 100;
	 else if (stateEq) s += 55;
	 else if (stateDiff) s -= 70;
	 return s;
   }
 
   function _isSameName(personInput, rec) {
	 var tf = _normalizeNamePart(personInput.first_name || '');
	 var tl = _normalizeNamePart(personInput.last_name || '');
	 if (!tf || !tl) return false;
 
	 var rn = _getRecordPrimaryName(rec);
	 return !!(rn && rn.first && rn.last && rn.first === tf && rn.last === tl);
   }
 
   // ----------------------------
   // Scoring (also scoped)
   // ----------------------------
   function _scoreWorkplaceRecord(personInput, rec) {
	 if (!rec) return 0;
 
	 var targetFirst = _normalizeNamePart(personInput.first_name || '');
	 var targetLast = _normalizeNamePart(personInput.last_name || '');
 
	 var recName = _getRecordPrimaryName(rec);
	 var recFirst = recName.first;
	 var recLast = recName.last;
 
	 var score = 0;
 
	 var hasTargetName = !!(targetFirst && targetLast);
	 var hasRecName = !!(recFirst && recLast);
	 var sameName = hasTargetName && hasRecName &&
	   targetFirst === recFirst && targetLast === recLast;
 
	 // Name scoring
	 if (sameName) {
	   score += 140;
	 } else if (hasTargetName && hasRecName) {
	   if (targetLast === recLast && targetFirst && recFirst && targetFirst.charAt(0) === recFirst.charAt(0)) {
		 score += 95;
	   } else {
		 if (targetLast === recLast) score += 60;
		 if (targetFirst === recFirst) score += 45;
	   }
	 } else if (targetLast && recLast && targetLast === recLast) {
	   score += 45;
	 }
 
	 // Input location
	 var inputCity = _normLower(personInput.city || '');
	 var inputState = _normLower(personInput.state || '');
 
	 // Address signals
	 var hasAddrCityState = _hasAnyAddressCityState(rec, inputCity, inputState);
	 var hasAddrState = _hasAnyAddressState(rec, inputState);
	 var hasAddrDiffState = _hasAnyAddressDifferentState(rec, inputState);
 
	 // Employment signals
	 var empLoc = _getEmploymentLoc(rec);
	 var empCityEq = !!(inputCity && empLoc.city && inputCity === empLoc.city);
	 var empStateEq = !!(inputState && empLoc.state && inputState === empLoc.state);
	 var empStateDiff = !!(inputState && empLoc.state && inputState !== empLoc.state);
 
	 // PersonLocation signals (light)
	 var persLoc = _getPersonLoc(rec);
	 var persCityEq = !!(inputCity && persLoc.city && inputCity === persLoc.city);
	 var persStateEq = !!(inputState && persLoc.state && inputState === persLoc.state);
	 var persStateDiff = !!(inputState && persLoc.state && inputState !== persLoc.state);
 
	 // Address score (strongest)
	 score += _stateCityScore(
	   !!(hasAddrCityState),
	   !!(hasAddrState),
	   !!(inputState && hasAddrDiffState && !hasAddrState)
	 );
 
	 // Employment score (second strongest)
	 score += _stateCityScore(
	   !!(empCityEq && empStateEq),
	   !!(empStateEq),
	   !!(empStateDiff)
	 );
 
	 // PersonLocation score (tie-breaker)
	 if (persCityEq && persStateEq) score += 20;
	 else if (persStateEq) score += 10;
	 else if (persStateDiff) score -= 15;
 
	 // Hard penalty: same-name + explicit wrong-state address/employment
	 if (sameName && inputState) {
	   var addrs = _getAllAddresses(rec);
	   if (addrs.length) {
		 if (!hasAddrState && hasAddrDiffState) {
		   score -= 140;
		 }
	   }
	   if (empStateDiff) {
		 score -= 60;
	   }
	 }
 
	 // Phone scoring (cannot override geography)
	 var inputPhoneDigits = _digitsOnly(personInput.phone || '');
	 var phoneExactMatch = false;
	 var phonePartialMatch = false;
 
	 if (inputPhoneDigits) {
	   var recPhones = _getRecordPhones(rec);
	   for (var i = 0; i < recPhones.length; i++) {
		 var rp = recPhones[i];
 
		 if (inputPhoneDigits.length === 10 && rp.length === 10 && inputPhoneDigits === rp) {
		   phoneExactMatch = true;
		   break;
		 }
 
		 if (inputPhoneDigits.length >= 7 && rp.length >= 7) {
		   var a = inputPhoneDigits.slice(-7);
		   var b = rp.slice(-7);
		   if (a === b) {
			 phonePartialMatch = true;
			 break;
		   }
		 }
	   }
	 }
 
	 var hasSomeStateAgreement = !!(hasAddrState || empStateEq || persStateEq);
	 if (phoneExactMatch) score += hasSomeStateAgreement ? 28 : 6;
	 else if (phonePartialMatch) score += hasSomeStateAgreement ? 18 : 4;
 
	 // Richness (cap so it cannot swamp identity/location)
	 var rich = _scoreRecordRichness(rec) || 0;
	 if (rich > 35) rich = 35;
	 score += rich;
 
	 return score;
   }
 
   // ----------------------------
   // Pick best (with same-name good-geo preference)
   // ----------------------------
   var inputState = _normLower(personInput && personInput.state);
   var bestRecord = null;
   var bestScore = -999999;
 
   var bestSameNameGoodGeo = null;
   var bestSameNameGoodGeoScore = -999999;
 
   for (var i = 0; i < records.length; i++) {
	 var rec = records[i];
	 if (!rec) continue;
 
	 var score = _scoreWorkplaceRecord(personInput, rec);
 
	 if (score > bestScore) {
	   bestScore = score;
	   bestRecord = rec;
	 }
 
	 if (inputState && _isSameName(personInput, rec)) {
	   var hasAddrState = _hasAnyAddressState(rec, inputState);
	   var empLoc = _getEmploymentLoc(rec);
	   var empStateEq = !!(empLoc.state && empLoc.state === inputState);
 
	   if (hasAddrState || empStateEq) {
		 if (score > bestSameNameGoodGeoScore) {
		   bestSameNameGoodGeoScore = score;
		   bestSameNameGoodGeo = rec;
		 }
	   }
	 }
   }
 
   if (bestSameNameGoodGeo) return bestSameNameGoodGeo;
 
   // keep your existing behavior
   if (bestScore < 40) return bestRecord;
 
   return bestRecord;
 }


export function formatAddress(identity) {
   if (!identity) return '--';
 
   function safe(val) {
	 return typeof val === 'string' ? val.trim() : '';
   }
 
   function cleanApt(val) {
	 // match your JSX: uppercase, then make "APT" look nicer
	 return val ? val.toUpperCase().replace(/\bAPT\b/g, 'Apt') : '';
   }
 
   // If we have display_full_street_address, treat it like the “street line”
   // (same as the JSX branch that uses _replaceAbbreviations(display_full_street_address))
   var hasDisplayStreet = !!safe(identity.display_full_street_address);
 
   var streetLine = '';
   if (hasDisplayStreet) {
	 streetLine = _capitalizeText(_replaceAbbreviations(safe(identity.display_full_street_address)));
   } else {
	 // fallback: address_1 is the street line
	 streetLine = safe(identity.address_1) ? _capitalizeText(safe(identity.address_1)) : '';
   }
 
   // Unit / Apt
   // If display_full_street_address exists, prefer address_2_normalized (like JSX),
   // otherwise use address_2.
   var unitRaw = hasDisplayStreet ? safe(identity.address_2_normalized) : safe(identity.address_2);
   var unit = cleanApt(unitRaw);
 
   // City/State/Zip:
   // If display_full_street_address exists, you typically show Neighborhood + Borough in the UI.
   // This function only returns a single string, so we map to "City-ish, State, Zip" as:
   // - city = neighborhood[0] if present, else city
   // - state = borough if present, else state
   // - zip = zip (same)
   var cityLike = '';
   if (hasDisplayStreet) {
	 cityLike = identity.neighborhood && identity.neighborhood[0] ? safe(identity.neighborhood[0]) : safe(identity.city);
   } else {
	 cityLike = safe(identity.city);
   }
 
   var stateLike = '';
   if (hasDisplayStreet) {
	 stateLike = safe(identity.borough) || safe(identity.state);
   } else {
	 stateLike = safe(identity.state);
   }
 
   var zip = safe(identity.zip);
 
   var parts = [
	 streetLine,
	 unit,
	 cityLike ? _capitalizeText(cityLike) : '',
	 stateLike ? _capitalizeText(stateLike) : '',
	 zip ? _capitalizeText(zip) : ''
   ];
 
   return parts.filter(Boolean).join(', ') || '--';
 }
export function _toStr(v) {
  if (v === null || typeof v === 'undefined') return '';
  return String(v);
}

export function _ensureHttp(url) {
   
   var u = _toStr(url).trim();
   if (!u) return '';
 
   // Already has a scheme (http, https, mailto, etc.)
   if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(u)) {
	 return u;
   }
 
   // Protocol-relative URL like //example.com
   if (u.indexOf('//') === 0) {
	 return 'https:' + u;
   }
 
   return 'https://' + u.replace(/^\/+/, '');
 }
 
 